import React, { useContext, useEffect } from 'react';
import fetchRetry from '../../utils/fetchRetry';
import { getBrowserErrorMessage } from '../../utils/browserUtils';
import getEnvConfig from '../../utils/config';
import { getIdToken } from '../../auth/AuthenticationContext';
import { getPuFrontendLogsUrl } from '../../utils/urlUtils';
import { resolveUserVersion } from '../../api/userVersion';
import { MonitoringContext } from '../../utils/monitoring-context/useMonitoring';
import { v4 } from 'uuid';

type WebGLProps = {
    webGLConfig?: string;
};

const WebGL = ({ webGLConfig }: WebGLProps) => {
    var monitoringContext = useContext(MonitoringContext);

    let sessionId: string | undefined = undefined;

    if (monitoringContext.sessionId) {
        console.log('Re-using RUM session Id');
        sessionId = monitoringContext.sessionId;
    } else {
        console.log('RUM Not configured. Will generate new Session Id');
        sessionId = v4();
    }

    window.pu_environment.session_id = sessionId;

    window.openAwsConsole = async (sessionUrl) => {
        const CONSOLE_SIGNOUT_URL = 'https://signin.aws.amazon.com/oauth?Action=logout';
        const tabName = 'AWSConsoleTab-' + Date.now();

        const consoleTab = window.open(CONSOLE_SIGNOUT_URL, tabName);
        if (!consoleTab) {
            console.error('openAwsConsole: unable to open a new tab (popup blocked?)');
            return;
        }
        await new Promise((resolve) => window.setTimeout(resolve, 1000));
        window.open(sessionUrl, tabName);
    };

    const notifyLoadEvent = async () => {
        const frontedLogsUrl = getPuFrontendLogsUrl();

        if (frontedLogsUrl) {
            const token = getIdToken();

            // Temporary hack: include frontend_version to segregate metrics for v1 vs v2.0+ users
            let isV2 = false;
            if (getEnvConfig().webgl_game_id === 'cloud-quest') {
                try {
                    const version = await resolveUserVersion(token);
                    isV2 = version !== 'V1';
                } catch (e) { /* ignore - best effort */ }
            }

            const event = {
                player_id: window.pu_environment.principal_id,
                session_id: sessionId,
                log_type: 'Datapoint',
                event_type: 'pre_webgl_load',
                time_stamp_unix: Math.floor(Date.now() / 1000),
                data: {
                    metric_name: 'pre_webgl_load',
                    ...(isV2 && { frontend_version: '0.0.2001' }),
                },
            };

            const records = {
                records: [JSON.stringify(event)],
            };

            const headers = new Headers();
            headers.append('Authorization', token);
            headers.append('Content-Type', 'application/json');

            var frontendLogsPayload = {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(records),
            };

            return fetchRetry(frontedLogsUrl, frontendLogsPayload);
        }
        return Promise.resolve();
    };

    const notifyWebGlFailedLoadEvent = (err: Error) => {
        const frontedLogsUrl = getPuFrontendLogsUrl();

        if (frontedLogsUrl) {
            const token = getIdToken();

            const event = {
                player_id: window.pu_environment.principal_id,
                session_id: sessionId,
                log_type: 'Events',
                event_type: 'webgl_load_failed',
                time_stamp_unix: Math.floor(Date.now() / 1000),
                data: {
                    message: err.toString(),
                    stack_trace: err.stack?.toString()
                }
            };

            const records = {
                records: [JSON.stringify(event)],
            };

            const headers = new Headers();
            headers.append('Authorization', token);
            headers.append('Content-Type', 'application/json');

            var frontendLogsPayload = {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(records),
            };

            return fetchRetry(frontedLogsUrl, frontendLogsPayload);
        }
        return Promise.resolve();
    };

    const [initializationFailed, setInitializationFailed] = React.useState<boolean>(false);

    const forceAspectRatio = getEnvConfig().force_aspect_ratio;

    useEffect(() => {
        if (!webGLConfig) {
            return;
        }

        const token = getIdToken();

        const headers = new Headers();
        headers.append('Authorization', token);

        var myInit = {
            method: 'GET',
            headers: headers,
        };

        fetchRetry(webGLConfig, myInit)
            .then((resp) => {
                return resp?.json();
            })
            .then((data) => {
                notifyLoadEvent();

                const myLoaderScriptElement = document.createElement('script');
                myLoaderScriptElement.src = data.loaderUrl;
                myLoaderScriptElement.id = 'myLoaderScript';
                myLoaderScriptElement.onload = () => {
                    // Check if Unity 6 loader is available first
                    if (window.MyUnity && typeof window.MyUnity.load === 'function') {
                        // Unity 6 loader
                        const canvas = document.querySelector('#unity-canvas') as HTMLCanvasElement;
                        if (!canvas) {
                            console.error('Canvas not found');
                            setInitializationFailed(true);
                            return;
                        }

                        const unityLoaderUrl = data.loaderUrl?.includes('MyLoader.js')
                            ? data.loaderUrl.replace(/MyLoader\.js$/, 'Build/WebGL.loader.js')
                            : data.loaderUrl;

                        window.MyUnity.load({
                            canvas,
                            loaderUrl: unityLoaderUrl,
                            dataUrl: data.dataUrl,
                            frameworkUrl: data.frameworkUrl,
                            codeUrl: data.codeUrl,
                            streamingAssetsUrl: data.streamingAssetsUrl,
                            symbolsUrl: data.symbolsUrl,
                            companyName: 'Amazon',
                            productName: 'Project Universe',
                            productVersion: data.productVersion,
                            devicePixelRatio:
                                typeof window.computeUnityStartupDevicePixelRatio === 'function'
                                    ? window.computeUnityStartupDevicePixelRatio({
                                          originalDevicePixelRatio: window.devicePixelRatio || 1,
                                          currentBrowserDevicePixelRatio: window.devicePixelRatio || 1,
                                          systemInfo: {},
                                          canvas,
                                      })
                                    : 1,
                            matchWebGLToCanvasSize: false,
                        }).catch((err: any) => {
                            console.log(err);
                            setInitializationFailed(true);
                        });
                    } else if (typeof window.createUnityInstance === 'function') {
                        // Legacy Unity loader
                        window.createUnityInstance(document.querySelector('#unity-canvas'), {
                            dataUrl: data.dataUrl,
                            frameworkUrl: data.frameworkUrl,
                            codeUrl: data.codeUrl,
                            streamingAssetsUrl: data.streamingAssetsUrl,
                            companyName: 'Amazon',
                            productName: 'Project Universe',
                            productVersion: data.productVersion,
                        })
                            .then((unityInstance: any) => {
                                window.myGameInstance = unityInstance;
                                window.devicePixelRatio = 1;
                            })
                            .catch((err: any) => {
                                console.log(err);
                                setInitializationFailed(true);
                            });
                    } else {
                        console.error('No Unity loader found');
                        setInitializationFailed(true);
                    }
                };
                document.body.appendChild(myLoaderScriptElement);
            })
            .catch((err) => {
                console.log(err);
                setInitializationFailed(true);
                notifyWebGlFailedLoadEvent(err);
            });
    }, [webGLConfig]);

    if (!webGLConfig) {
        return (
            <div className="cq-content">
                <p>No WebGL build provided.</p>
            </div>
        );
    }

    return initializationFailed ? (
        <div className="cq-content">
            <p>An error has occurred.</p>
            <p>{getBrowserErrorMessage()}</p>
        </div>
    ) : (
        <div className={forceAspectRatio ? 'webgl-wrapper' : 'webgl-wrapper-no-ratio'}>
            <div className="aspect"></div>
            <div className="webgl-content">
                <div id="unityContainer">
                    <canvas id="unity-canvas" style={{ background: '#000000' }}></canvas>
                </div>
            </div>
        </div>
    );
};

export default WebGL;
